import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TenantPage from 'components/common/TenantPage';
import { contractEquipmentService } from 'services/api';
import { EnhancedDataGrid } from 'components/common/enhanced/data-grid';
import {
  DataGridPremiumProps,
  GridActionsCellItem,
  GridColDef,
  GridColumns,
  GridRowParams
} from '@mui/x-data-grid-premium';
import { Button, ButtonGroup } from '@mui/material';
import { filterOperators, dateFilterOperators } from '../../../../constants';
import { baseCell } from 'components/common/enhanced/cells/base-cell';
import { MaintenanceColumns } from 'components/common/enhanced/common-headers';
import MaintenanceDetail from '../detail/MaintenanceDetail';
// import ScheduleIcon from '@mui/icons-material/Schedule';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import { isTenantAdministrator } from 'components/helpers/IsTenantAdministrator';
import { Link, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { MaintenanceMonthDataDialog } from './MaintenanceMonthDataDialog';
import { isSupplier } from 'components/helpers/IsSupplier';
import { isTechnician } from 'components/helpers/IsTechnician';
import { isDirector } from 'components/helpers/IsDirector';
import CloseMaintenanceDialog from './CloseMaintenanceDialog';
import { IMaintenance } from 'interfaces';
import toast from 'features/toast';
import MultipleDatesPicker from '@ambiot/material-ui-multiple-dates-picker';
import { useAppSelector } from 'app/store';

export const Maintenance = () => {
  const translationPrefix = 'pages.maintenance';
  const isDir = isDirector();

  const { t } = useTranslation();
  const datagridRefresh = useRef(null);

  const [contractEquipment, setContractEquipment] = useState<number | null>(null);
  const [searchParams] = useSearchParams();

  // Evaluate role selectors unconditionally to keep React hook order stable.
  const tenantAdministrator = isTenantAdministrator();
  const director = isDirector();
  const technician = isTechnician();
  const supplier = isSupplier();

  const archiveCanSee = tenantAdministrator || director;
  const userWarehouse = useAppSelector((state) => state.auth.warehouse);
  const lockWarehouseFilter = Boolean(userWarehouse) && technician;

  // ----------------------------------------------------  SINGLE DATE
  const [openMultiDate, setOpenMultiDate] = useState(false);

  const onOpenMultiDateCalendar = (contractEquipmentId: number) => {
    setContractEquipment(contractEquipmentId);
    setOpenMultiDate(true);
  };

  const onCancelMultiDateCalendar = () => {
    setOpenMultiDate(false);
    setContractEquipment(null);
  };

  const onSubmitMultiDate = (values: string[]) => {
    const formattedDates = values.map((dateStr) => {
      const date = new Date(dateStr);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    });
    contractEquipmentService.addMaintenanceDates(contractEquipment, formattedDates).then(() => {
      datagridRefresh.current();
      onCancelMultiDateCalendar();
    });
  };
  // ----------------------------------------------------  MULTI MONTHs
  const [openExpectedMultiMonthCalendar, setOpenExpectedMultiMonthCalendar] = useState(false);

  const onOpenExpectedMultiMonthCalendar = (contractEquipmentId: number) => {
    setOpenExpectedMultiMonthCalendar(true);
    setContractEquipment(contractEquipmentId);
  };

  const onCancelExpectedMultiMonth = () => {
    setOpenExpectedMultiMonthCalendar(false);
    setContractEquipment(null);
  };

  const onSubmitMultipleMonth = async (values: {
    date: string;
    maintenance_type?: number | null;
  }) => {
    const formattedDate = dayjs(values.date).format('YYYY-MM-DD');

    contractEquipmentService
      .addMaintenanceDates(contractEquipment, [formattedDate], values.maintenance_type ?? null)
      .then(() => {
        toast.success(t(`actions.successfully.created`));
        setContractEquipment(null);
        datagridRefresh.current();
      });
    setOpenExpectedMultiMonthCalendar(false);
  };

  // ---------------------------------------------------- Close Maintenance Dialog
  const [openCloseMaintenance, setOpenCloseMaintenance] = useState(false);
  const [maintenanceToClose, setMaintenanceToClose] = useState<IMaintenance | null>(null);

  const setOpenCloseMaintenanceDialog = (row: IMaintenance) => {
    setOpenCloseMaintenance(true);
    setMaintenanceToClose(row);
  };

  const setCloseMaintenanceDialog = () => {
    setOpenCloseMaintenance(false);
    setMaintenanceToClose(null);
  };

  // ---------------------------------------------------- Maintenance Detail and Columns
  const isDisabledEdit = technician || director || supplier;

  const aggregatedColumns: GridColumns = [
    {
      field: 'equipment',
      headerName: t(`global.equipment`),
      width: 250,
      filterOperators,
      valueGetter: (params) => params.row.equipment_code,
      renderCell: baseCell
    },
    {
      field: 'description',
      headerName: t(`global.description`),
      width: 300,
      filterOperators,
      renderCell: baseCell
    },
    {
      field: 'serial_number',
      headerName: t(`entities.equipment.serial_number`),
      width: 200,
      filterOperators,
      renderCell: baseCell
    },
    {
      field: 'warehouse_code',
      headerName: t(`global.warehouse`),
      width: 200,
      filterOperators,
      filterable: !lockWarehouseFilter,
      sortable: false,
      renderCell: baseCell,
      hide: isDir
    },
    {
      field: 'equipment_position',
      headerName: t('global.equipment_position'),
      width: 200,
      filterOperators,
      sortable: false,
      hide: isDir,
      renderCell: baseCell,
      valueGetter: (params) => params.row?.position ?? ''
    },
    // {
    //   field: 'position',
    //   headerName: t(`entities.equipment.position`),
    //   width: 200,
    //   filterOperators,
    //   renderCell: baseCell
    // },
    {
      field: 'supplier',
      headerName: t(`global.supplier`),
      width: 200,
      filterOperators,
      renderCell: baseCell
    },
    {
      field: 'assistance_supplier',
      headerName: t(`entities.equipment.assistance_supplier`),
      width: 200,
      filterOperators,
      sortable: false,
      renderCell: baseCell
    },
    {
      field: 'maintenance_type',
      headerName: t(`entities.maintenance.maintenance_type`),
      width: 220,
      filterOperators,
      sortable: false,
      valueGetter: (params) => params.row?.maintenance_type?.display_name || '',
      renderCell: (params) =>
        baseCell({
          ...params,
          value: params.value
            ? t(`entities.maintenance.maintenance_type_options.${params.value}`)
            : ''
        } as any)
    },
    {
      field: 'expected_month',
      headerName: t(`entities.equipment.expected_month`),
      width: 200,
      filterOperators: dateFilterOperators,
      renderCell: baseCell
    }
  ];
  if (!isDisabledEdit) {
    aggregatedColumns.push({
      field: 'actions',
      type: 'actions',
      headerName: t(`global.actions`),
      getActions: (params: GridRowParams) => {
        const actions = [
          <GridActionsCellItem
            key={1}
            label="Calendario"
            icon={<EventAvailableIcon />}
            onClick={() => onOpenExpectedMultiMonthCalendar(params.row.contract_equipment_id)}
          />
          // <GridActionsCellItem
          //   key={2}
          //   icon={<ScheduleIcon />}
          //   label={t(`${translationPrefix}.close`)}
          //   onClick={() => onOpenMultiDateCalendar(params.row.contract_equipment_id)}
          // />
        ];
        return actions;
      },
      filterable: false
    });
  }

  const columns: GridColDef[] = MaintenanceColumns(setOpenCloseMaintenanceDialog);

  const getDetailPanelContent = useCallback<
    NonNullable<DataGridPremiumProps['getDetailPanelContent']>
  >(({ row }) => <MaintenanceDetail row={{ ...row }} columns={columns} />, [columns]);

  return (
    <>
      <TenantPage
        title={t(`${translationPrefix}.title`)}
        subtitle={t(`${translationPrefix}.subtitle`)}
        menuRight={
          <div className="flex justify-end items-center">
            <ButtonGroup>
              <Link to="waiting" className="no-underline">
                <Button variant="outlined" size="small">
                  {t(`${translationPrefix}.waiting`)}
                </Button>
              </Link>
              <Link to="expiring">
                <Button variant="outlined" size="small">
                  {t(`${translationPrefix}.expiring`)}
                </Button>
              </Link>
              {archiveCanSee && (
                <Link to="archive">
                  <Button variant="outlined" size="small">
                    {t(`global.archive`)}
                  </Button>
                </Link>
              )}
            </ButtonGroup>
          </div>
        }>
        <EnhancedDataGrid
          columns={aggregatedColumns}
          service={contractEquipmentService}
          getMethod={contractEquipmentService.aggregatedContract}
          initialParams={{ equipment_id: searchParams.get('equipment_id') }}
          refresh={datagridRefresh}
          getDetailPanelContent={getDetailPanelContent}
          disableDelete
        />

        <CloseMaintenanceDialog
          closeDialog={setCloseMaintenanceDialog}
          datagridRefresh={datagridRefresh}
          id={maintenanceToClose?.id}
          maintenance={maintenanceToClose}
          open={openCloseMaintenance}
        />
        {/* MULTI MONTH DIALOG*/}
        <MaintenanceMonthDataDialog
          openCalendar={openExpectedMultiMonthCalendar}
          onCancel={onCancelExpectedMultiMonth}
          onSubmit={onSubmitMultipleMonth}
        />

        <MultipleDatesPicker
          open={openMultiDate}
          onCancel={onCancelMultiDateCalendar}
          onSubmit={onSubmitMultiDate}
        />
      </TenantPage>
    </>
  );
};

export default Maintenance;
